import { ItemRenderer } from './ItemRenderer.jsx'
import { SaveFailure } from './SaveFailure.jsx'
import { optionsOf, resolveKind, isRenderable, optionDisplay, KIND } from './kinds.js'

/**
 * Many items sharing one instruction and one scale, one row per item.
 * Only for questionnaires config marks griddable: true.
 *
 * A grid is only honest when every item really does share one scale, so this
 * checks rather than assumes. AUDIT and EPDS fail it on their own merits — their
 * options differ per item — but the check is on the data, not on the name.
 */
export function canGrid(items, formats) {
  if (!Array.isArray(items) || items.length === 0) return false

  const formatId = items[0].response_format
  if (!items.every((item) => item.response_format === formatId)) return false

  const format = formats?.[formatId]
  if (!isRenderable(format)) return false
  if (format.free_text) return false

  // Only option kinds line up in columns. A number or a time box never grids.
  const kind = resolveKind(format)
  if (kind !== KIND.ORDINAL && kind !== KIND.BINARY) return false

  /*
   * WIDTH IS NOT DECIDED HERE ANY MORE, and that is the AD-8 fix.
   *
   * This used to return `options.length <= 6` — a constant derived at a 16px
   * root. The cell floor is 3rem and scales with her text while the content
   * column does not, so above 16px the grid wrapped to a second line while the
   * sticky header did not, and every column label re-mapped to the wrong
   * answer. See gridCapacity.js for the measurements.
   *
   * So canGrid answers only the question it can answer from DATA — do these
   * items genuinely share one scale, and is it an option kind — and the CALLER
   * asks gridCapacity() whether that many columns fit at the current text size.
   * Two different questions that were being answered by one number.
   */
  return true
}

/**
 * How many options a section would need columns for. Exported so the caller can
 * compare it against the live capacity without reaching into formats itself.
 */
export function gridColumnsNeeded(items, formats) {
  const format = formats?.[items?.[0]?.response_format]
  return optionsOf(format).length
}

export function Grid({
  questionnaire,
  items,
  formats,
  answers,
  onAnswer,
  disabled,
  // Item ids the server is still waiting for. Marked so a participant can see
  // which row is missing instead of hunting a 20-item grid for a blank one.
  highlightIds = [],
  // DS-2 §3.9. A failed save on a grid row is the worst place to raise it to
  // the screen: twenty rows, one bad one, and a message at the bottom naming
  // none of them.
  failures = null,
  onRetry = null,
}) {
  const outstanding = new Set(highlightIds)
  const format = formats[items[0].response_format]
  const options = optionsOf(format)
  const columns = { gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }

  /*
   * §4.2 — THE HEADER HEIGHT IS COMPUTED ONCE PER SECTION AND FIXED.
   *
   * Labels are bottom-aligned, which is the single change that fixes F7's
   * ragged 65px skyline: "At no time" is one line and "Less than half of the
   * time" is four, so top-aligning them left every column starting at a
   * different height. Bottom-aligned, they all sit on a common baseline just
   * above the first row.
   *
   * Fixing the height matters because the header is sticky and the section
   * paginates: a height derived from whatever is on screen would jump as she
   * pages through one instrument. Three lines is the cap — --t-column at
   * 0.8125rem/1.25 gives 3 x 16.25px, plus the 8px of vertical padding.
   */
  const worstLines = Math.min(
    3,
    options.reduce((max, o) => Math.max(max, Math.ceil(optionDisplay(o).text.length / 12)), 1),
  )
  // 0.6875rem * 1.3 line-height, matching .t-column exactly - a mismatch here
  // either clips the third line or leaves dead space above every grid.
  const headerHeight = `calc(${worstLines} * 0.6875rem * 1.3 + var(--s-4))`

  return (
    <div>
      {questionnaire?.instruction ? (
        <p className="t-instruction mb-[var(--s-4)]">{questionnaire.instruction}</p>
      ) : null}

      {/*
        THE SECTION IS ONE SURFACE (§4.2). Thirty floating bordered boxes become
        one object with divisions — the same inset-grouped model as §4.1, at
        length. The surface carries the border and the radius; rows carry only a
        --rule separator.

        overflow-visible for the same reason as the OptionGroup: a clipped
        overflow would eat the focus ring.
      */}
      <div className="overflow-visible rounded-[var(--r-surface)] border border-[var(--line)] bg-[var(--surface-raised)]">
        {/*
          The scale is drawn ONCE and stays put while the rows scroll. Full
          labels, not endpoint anchors: a participant has to be able to tell
          what the middle columns mean.
        */}
        <div
          className={
            /*
             * items-CENTER, not items-end. Labels wrap to different depths
             * ("At no time" is two lines where "Less than half the time" is
             * three), and bottom-aligning them left every shorter label
             * floating at its own height - the "titles are not centered"
             * complaint, literally. Centring in a fixed-height header gives
             * every label the same optical anchor regardless of wrap depth.
             */
            'sticky top-0 z-10 grid items-center gap-[var(--s-2)] ' +
            'rounded-t-[var(--r-surface-inner)] border-b border-[var(--line)] ' +
            /*
             * THE HEADER'S HORIZONTAL PADDING MUST EQUAL THE ROW'S.
             *
             * It was --s-3 against the rows' --s-4, so every column label sat
             * 4px off the cells it labels. On a grid that is the one alignment
             * that has to be exact — the header IS the only place the column
             * meanings are written, so a label drifting off its own column is
             * the whole control lying.
             *
             * Matching it also widens each column by 4px, which is what stops
             * "Moderate" being chopped mid-word by the wrap guard below.
             */
            'bg-[var(--surface)] px-[var(--s-4)] py-[var(--s-2)]'
          }
          style={{ ...columns, minHeight: headerHeight }}
        >
          {options.map((option) => (
            <div
              key={String(option.value)}
              className="t-column text-center text-[var(--ink-700)]"
            >
              {optionDisplay(option).text}
            </div>
          ))}
        </div>

        <ul>
          {items.map((item, i) => (
            <li
              key={item.item_id}
              data-item-id={item.item_id}
              className={
                /*
                 * ROW RHYTHM (§4.2 spacing rules, stated once):
                 *   - px matches the header exactly (--s-4): the labels above
                 *     ARE these columns' meanings, so horizontal alignment is
                 *     correctness, not taste.
                 *   - py steps up from --s-3 to --s-4: the prompt and its radio
                 *     row are ONE unit, and the unit needs more air between
                 *     itself and its neighbours than it has inside itself.
                 *     Internal gap (prompt to radios) stays --s-2, so the rule
                 *     inside < outside is what groups them visually.
                 */
                'px-[var(--s-4)] py-[var(--s-4)] ' +
                (i === 0 ? '' : 'border-t border-[var(--rule)] ') +
                /*
                 * ZEBRA, QUIETLY. Six identical circles per row, five rows: the
                 * eye needs a horizontal guide to keep a row's circles attached
                 * to the right prompt. A one-step tint on alternate rows does
                 * that without adding a single line. --surface-sunken is the
                 * quietest step the palette has; anything louder starts ranking
                 * the rows.
                 */
                (i % 2 === 1 && !outstanding.has(item.item_id) ? 'bg-[var(--surface-sunken)]/60 ' : '') +
                (i === items.length - 1 ? 'rounded-b-[var(--r-surface-inner)] ' : '') +
                /*
                  A flagged row keeps the 2px --critical left rule and
                  aria-invalid (DS-2 §3.8). It cannot collide with §1's accent
                  leading bar: a flagged item is by definition unanswered, so no
                  cell inside it can be selected.
                */
                (outstanding.has(item.item_id)
                  ? 'border-l-2 border-l-[var(--critical)] bg-[var(--surface-sunken)]'
                  : '')
              }
              aria-invalid={outstanding.has(item.item_id) ? true : undefined}
            >
              {/*
                The prompt sits above its cell row rather than beside it. Beside
                it leaves roughly 30px a column on a phone, which is a mis-tap
                waiting to happen; above it gives the row the full screen width.
              */}
              <p
                id={`${item.item_id}-prompt`}
                className="t-question mb-[var(--s-2)] text-[var(--ink-900)]"
              >
                {item.prompt}
              </p>
              <ItemRenderer
                item={item}
                format={formats[item.response_format]}
                answer={answers?.[item.item_id] ?? null}
                onChange={(next) => onAnswer(item, next)}
                disabled={disabled}
                variant="row"
                showPrompt={false}
                ariaLabelledBy={`${item.item_id}-prompt`}
              />
              <SaveFailure
                error={failures?.[item.item_id] ?? null}
                onRetry={onRetry ? () => onRetry(item) : null}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
